/**
 * Target Progress Card Component
 *
 * Displays improvement target with:
 * - Target score and current score
 * - Gap analysis
 * - Timeline and days remaining
 * - Status indicator (on track / at risk / overdue / achieved)
 * - Expected ROI
 * - Required monthly improvement rate
 *
 * Design: Actionable card with clear progress tracking
 */

import React from 'react';

// ============================================================================
// TYPES
// ============================================================================

interface TargetProgressCardProps {
  targetScore: number;        // 0-100
  currentScore: number;       // 0-100
  targetDate: string;         // ISO date
  daysRemaining: number;
  status: 'on_track' | 'at_risk' | 'overdue' | 'achieved';
  gap: number;                // Points to improve
  progressPct: number;        // 0-100
  expectedROI?: number;       // Dollar value
  rationale?: string;
  requiredMonthlyRate?: number; // Points per month needed
}

// ============================================================================
// STATUS METADATA
// ============================================================================

const STATUS_META: Record<string, { label: string; color: string; emoji: string; description: string }> = {
  achieved: {
    label: 'Achieved',
    color: '#27AE60',
    emoji: '🎉',
    description: 'Target reached!'
  },
  on_track: {
    label: 'On Track',
    color: '#3498DB',
    emoji: '✅',
    description: 'Progressing well'
  },
  at_risk: {
    label: 'At Risk',
    color: '#F39C12',
    emoji: '⚠️',
    description: 'Needs attention'
  },
  overdue: {
    label: 'Overdue',
    color: '#E74C3C',
    emoji: '🚨',
    description: 'Past deadline'
  }
};

// ============================================================================
// HELPERS
// ============================================================================

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

// ============================================================================
// COMPONENT
// ============================================================================

export const TargetProgressCard: React.FC<TargetProgressCardProps> = ({
  targetScore,
  currentScore,
  targetDate,
  daysRemaining,
  status,
  gap,
  progressPct,
  expectedROI,
  rationale,
  requiredMonthlyRate
}) => {
  const statusMeta = STATUS_META[status];
  const isAchieved = status === 'achieved';
  const isOverdue = status === 'overdue';

  return (
    <div className="target-progress-card">
      {/* Header */}
      <div className="target-header">
        <div className="target-title">
          <h3>Improvement Target</h3>
          <div className="status-badge" style={{ background: statusMeta.color }}>
            <span className="status-emoji">{statusMeta.emoji}</span>
            <span className="status-label">{statusMeta.label}</span>
          </div>
        </div>
        <div className="target-subtitle">{statusMeta.description}</div>
      </div>

      {/* Score Comparison */}
      <div className="score-comparison">
        <div className="score-item current">
          <div className="score-label">Current</div>
          <div className="score-value">{currentScore.toFixed(1)}</div>
        </div>
        <div className="score-arrow">→</div>
        <div className="score-item target">
          <div className="score-label">Target</div>
          <div className="score-value">{targetScore.toFixed(1)}</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="target-progress-bar">
        <div
          className="progress-fill"
          style={{
            width: `${Math.min(100, progressPct)}%`,
            background: statusMeta.color
          }}
        />
        <div className="progress-label">{progressPct.toFixed(0)}% Complete</div>
      </div>

      {/* Key Metrics Grid */}
      <div className="metrics-grid">
        {/* Gap */}
        <div className="metric-item">
          <div className="metric-label">Gap</div>
          <div className={`metric-value ${isAchieved ? 'achieved' : ''}`}>
            {isAchieved ? '0' : gap.toFixed(1)} pts
          </div>
        </div>

        {/* Days Remaining */}
        <div className="metric-item">
          <div className="metric-label">
            {isOverdue ? 'Days Overdue' : 'Days Remaining'}
          </div>
          <div className={`metric-value ${isOverdue ? 'overdue' : ''}`}>
            {Math.abs(daysRemaining)}
          </div>
        </div>

        {/* Target Date */}
        <div className="metric-item">
          <div className="metric-label">Target Date</div>
          <div className="metric-value date">{formatDate(targetDate)}</div>
        </div>

        {/* Monthly Rate */}
        {requiredMonthlyRate !== undefined && !isAchieved && (
          <div className="metric-item">
            <div className="metric-label">Required Rate</div>
            <div className="metric-value rate">
              {requiredMonthlyRate.toFixed(1)} pts/mo
            </div>
          </div>
        )}
      </div>

      {/* ROI Display */}
      {expectedROI && (
        <div className="roi-display">
          <div className="roi-icon">💰</div>
          <div className="roi-content">
            <div className="roi-label">Expected Value</div>
            <div className="roi-value">{formatCurrency(expectedROI)}</div>
          </div>
        </div>
      )}

      {/* Rationale */}
      {rationale && (
        <div className="target-rationale">
          <div className="rationale-label">Rationale</div>
          <div className="rationale-text">{rationale}</div>
        </div>
      )}

      {/* Action Hint */}
      {!isAchieved && (
        <div className={`action-hint ${status}`}>
          {status === 'at_risk' && '⚡ Accelerate improvement efforts'}
          {status === 'overdue' && '🔔 Target date has passed - consider revision'}
          {status === 'on_track' && '👍 Maintain current pace'}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// STYLES (CSS - Add to styles.css)
// ============================================================================

/*
.target-progress-card {
  padding: 2rem;
  background: linear-gradient(135deg, #0f1730 0%, #1a2844 100%);
  border: 1px solid #1c2a54;
  border-radius: 12px;
  margin: 2rem 0;
}

.target-header {
  margin-bottom: 1.5rem;
}

.target-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.target-title h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #e8eefc;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
}

.target-subtitle {
  font-size: 0.875rem;
  color: #8ea1c7;
}

.score-comparison {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 1.5rem;
  background: rgba(15, 23, 48, 0.5);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.score-item {
  text-align: center;
}

.score-item.current {
  opacity: 0.8;
}

.score-label {
  font-size: 0.75rem;
  color: #8ea1c7;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.score-value {
  font-size: 2rem;
  font-weight: 700;
  color: #e8eefc;
}

.score-arrow {
  font-size: 2rem;
  color: #8ea1c7;
}

.target-progress-bar {
  position: relative;
  height: 24px;
  background: rgba(15, 23, 48, 0.8);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 1.5rem;
  border: 1px solid #1c2a54;
}

.progress-fill {
  height: 100%;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-label {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.metric-item {
  padding: 1rem;
  background: rgba(15, 23, 48, 0.5);
  border-radius: 8px;
  border: 1px solid #1c2a54;
  text-align: center;
}

.metric-label {
  font-size: 0.75rem;
  color: #8ea1c7;
  margin-bottom: 0.5rem;
}

.metric-value {
  font-size: 1.25rem;
  font-weight: 700;
  color: #e8eefc;
}

.metric-value.achieved {
  color: #27AE60;
}

.metric-value.overdue {
  color: #E74C3C;
}

.metric-value.date {
  font-size: 1rem;
}

.metric-value.rate {
  font-size: 1rem;
  color: #F39C12;
}

.roi-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(90deg, rgba(243, 156, 18, 0.1), rgba(243, 156, 18, 0.05));
  border: 1px solid rgba(243, 156, 18, 0.3);
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.roi-icon {
  font-size: 2rem;
}

.roi-label {
  font-size: 0.75rem;
  color: #8ea1c7;
  margin-bottom: 0.25rem;
}

.roi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #F39C12;
}

.target-rationale {
  padding: 1rem;
  background: rgba(15, 23, 48, 0.5);
  border-radius: 8px;
  border-left: 3px solid #3498DB;
  margin-bottom: 1rem;
}

.rationale-label {
  font-size: 0.75rem;
  color: #8ea1c7;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.rationale-text {
  font-size: 0.875rem;
  color: #e8eefc;
  line-height: 1.5;
}

.action-hint {
  text-align: center;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
}

.action-hint.on_track {
  background: rgba(52, 152, 219, 0.1);
  color: #3498DB;
  border: 1px solid rgba(52, 152, 219, 0.3);
}

.action-hint.at_risk {
  background: rgba(243, 156, 18, 0.1);
  color: #F39C12;
  border: 1px solid rgba(243, 156, 18, 0.3);
}

.action-hint.overdue {
  background: rgba(231, 76, 60, 0.1);
  color: #E74C3C;
  border: 1px solid rgba(231, 76, 60, 0.3);
}
*/
